/**
 * Project Members Component - Manage users in a project
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { projectAPI } from '@/api/projectAPI';
import { teamAPI } from '@/api/teamAPI';
import { ProjectMember, Team, Project, User } from '@/types';
import {
  Users,
  Loader2,
  UsersRound,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Loader } from '@/components/Loader';
import { AddTeamsModal, MemberRole } from './AddTeamsModal';
import { MembersTable } from './MembersTable';
import { userAPI } from '@/api/userAPI';
import { AddMembersModal } from './AddMembersModal';

interface ProjectMembersProps {
  projectId: string;
}

export const ProjectMembers = ({ projectId }: ProjectMembersProps) => {
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [project, setProject] = useState<Project | null>(null);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [viewHeight, setViewHeight] = useState<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{ id: string; email: string, team_id:string|null } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  // Multi-team state
  const [assignedTeams, setAssignedTeams] = useState<Team[]>([]);
  const [showRemoveTeamModal, setShowRemoveTeamModal] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState<Team | null>(null);
  const [isRemovingTeam, setIsRemovingTeam] = useState(false);


  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);

  // Determine permissions based on main/global role
  const globalRole = currentUser?.role;
  const isMainAdmin = globalRole === 'admin';

  // Load project details and members
  useEffect(() => {
    const loadProject = async () => {
      try {
        // Invalidate cache first to ensure fresh data (especially after team deletion)
        projectMemberAPI.invalidateCache(projectId);
        const projectData = await projectAPI.getProject(projectId);
        setProject(projectData);
      } catch (error) {
        console.error('Failed to load project:', error);
      }
    };
    loadProject();
  }, [projectId]);

  // Load members
  useEffect(() => {
    setMembersLoaded(false);
    setPage(1);
    loadMembers(1);
  }, [projectId]);

  // Compute internal view height for scrollable area
  useEffect(() => {
    const recompute = () => {
      if (!contentRef.current) return;
      const rect = contentRef.current.getBoundingClientRect();
      const headerHeight = 120;
      const bottomPadding = 32;
      const avail = window.innerHeight - rect.top - headerHeight - bottomPadding;
      setViewHeight(Math.max(300, avail));
    };

    const timeoutId = window.setTimeout(recompute, 100);
    window.addEventListener('resize', recompute);
    window.addEventListener('orientationchange', recompute);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', recompute);
      window.removeEventListener('orientationchange', recompute);
    };
  }, []);
  // Aggregate bootloading similar to Kanban/Summary
  useEffect(() => {
    if (membersLoaded) setBootLoading(false);
    else setBootLoading(true);
  }, [membersLoaded]);

  // Derive assigned teams from members whenever members change
  useEffect(() => {
    const loadAssignedTeams = async () => {
      // Collect unique team IDs from team-type members
      const teamIds = Array.from(
        new Set(
          members
            .filter((m) => m.membership_type === 'team' && m.team_id && m.team_id !== 'None')
            .map((m) => m.team_id as string)
        )
      );
      if (teamIds.length === 0) {
        setAssignedTeams([]);
        return;
      }
      try {
        // Fetch team details for each unique team ID
        const teamPromises = teamIds.map((tid) => teamAPI.getTeam(tid).catch(() => null));
        const teams = (await Promise.all(teamPromises)).filter(Boolean) as Team[];
        setAssignedTeams(teams);
      } catch {
        console.error('Failed to load assigned teams');
      }
    };
    if (membersLoaded) {
      loadAssignedTeams();
    }
  }, [members, membersLoaded]);

  const loadMembers = async (toPage?: number) => {
    try {
      setLoading(true);
      const currentPage = toPage ?? page;
      // Fetch total count once and on project change
      try {
        const count = await projectMemberAPI.getMemberCount(projectId);
        setTotalCount(count || 0);
      } catch (e) {
        // Fallback if /count endpoint is not available: fetch all once and use length
        try {
          const all = await projectMemberAPI.getProjectMembers(projectId, true);
          setTotalCount(all.length);
        } catch { }
      }
      const skip = (currentPage - 1) * pageSize;
      const data = await projectMemberAPI.getProjectMembers(projectId, true, { skip, limit: pageSize });
      setMembers(data);
      setMembersLoaded(true);
    } catch (error) {
      console.error('Failed to load members:', error);
      toast.error('Failed to load project members');
    } finally {
      setLoading(false);
      setMembersLoaded(true);
    }
  };

  const loadAvailableTeams = async () => {
    if (!project?.organization_id) {
      toast.error('Project organization not found');
      return;
    }
    try {
      setTeamsLoading(true);
      const teams = await teamAPI.getTeams({ organization_id: project.organization_id });
      // Filter out teams already assigned to this project
      const assignedTeamIds = new Set(assignedTeams.map((t) => t.id));
      const filtered = teams.filter((t) => !assignedTeamIds.has(t.id));
      setAvailableTeams(filtered);
    } catch (error) {
      console.error('Failed to load teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setTeamsLoading(false);
    }
  };

  const handleAssignTeam = async (teamIdOrIds: string | string[]) => {
    if (!isMainAdmin) {
      toast.error('Only admins can assign teams');
      return;
    }
    // Since allowMultiple is false, we expect a single team ID
    const teamId = typeof teamIdOrIds === 'string' ? teamIdOrIds : teamIdOrIds[0];
    if (!teamId) {
      toast.error('Please select a team');
      return;
    }

    try {
      const response = await projectAPI.assignTeamToProject(projectId, teamId);
      // Invalidate cache so all views get fresh data
      projectMemberAPI.invalidateCache(projectId);
      toast.success('Team assigned successfully. All team members have been added to the project.');
      setShowAssignTeamModal(false);
      
      // Reload members to show newly added team members
      try {
        const count = await projectMemberAPI.getMemberCount(projectId);
        setTotalCount(count || 0);
        const totalPages = Math.max(1, Math.ceil((count || 0) / pageSize));
        setPage(totalPages);
        await loadMembers(totalPages);
      } catch {
        await loadMembers();
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        const detail = error.response.data?.detail;
        // console.log("conflict detail ------->", detail);

        if (detail?.has_conflicts && detail?.conflicts?.length > 0) {
          // Build a readable message for each conflicting user
          const conflictMessages = detail.conflicts
            .map((c: any) => `• ${c.username
            } (${c.user_email})`)
            .join('\n');

          toast.error(
            `Cannot assign team. These users are already direct members:\n${conflictMessages}`,
            { duration: 6000 } // show longer so user can read
          );
        } else {
          toast.error(detail?.message || 'Conflict: Team cannot be assigned.');
        }
        return; // stop here, don't close modal
      }
      toast.error(error.response?.data?.detail || 'Failed to assign team');
    }
  };

  const openAssignTeamModal = async () => {
    await loadAvailableTeams();
    setShowAssignTeamModal(true);
  };

  const handleRemoveMember = async (member: { id: string; email: string, team_id:string|null }) => {
    
    try {
      setIsRemoving(true);
      // await projectMemberAPI.removeMember(memberId);
      // Invalidate cache so all views get fresh data
      projectMemberAPI.invalidateCache(projectId);

      const payload= {
        user_id:member.id,
        team_id:member.team_id
      }
      // Recalculate paging in case we removed the last item on the last page
      try {
        const response =await projectMemberAPI.deleteMembersFromProject(projectId, payload)
        // console.log("response---->", response)
        toast.success('Member removed from project');
        await refreshMembers()
        // const count = await projectMemberAPI.getMemberCount(projectId);
        // setTotalCount(count || 0);
        // const totalPages = Math.max(1, Math.ceil((count || 0) / pageSize));
        // const newPage = Math.min(page, totalPages);
        // setPage(newPage);
        // await loadMembers(newPage);
      } catch {
      }
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error('Failed to remove member');
    }finally {
     setIsRemoving(false);  // STOP LOADING
  }
  };

  const handleUpdateRole = async (memberId: string, newRole: 'admin' | 'member' | 'viewer') => {
    if (!isMainAdmin) {
      toast.error('Only admins can change roles');
      return;
    }
    try {
      await projectMemberAPI.updateMember(memberId, newRole);
      // Invalidate cache so all views get fresh data
      projectMemberAPI.invalidateCache(projectId);
      toast.success('Member role updated');
      loadMembers();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error('Failed to update member role');
    }
  };

  const handleRemoveTeam = async () => {
    if (!teamToRemove) return;
    try {
      setIsRemovingTeam(true);
      await projectAPI.removeTeamFromProject(projectId, teamToRemove.id);
      projectMemberAPI.invalidateCache(projectId);
      toast.success(`Team "${teamToRemove.name}" removed from project`);
      setShowRemoveTeamModal(false);
      setTeamToRemove(null);
      await refreshMembers();
    } catch (error: any) {
      console.error('Failed to remove team:', error);
      toast.error(error?.response?.data?.detail || 'Failed to remove team from project');
    } finally {
      setIsRemovingTeam(false);
    }
  };

  if (bootLoading) {
    return <Loader variant="overlay" size="xl" text="Loading members..." />;
  }

  const openMemberModal = async() => {
    if (!project || !project.organization_id) {
      toast.error('Project organization not found');
      return;
    }
    try {
      setUsersLoading(true);
      setMemberModalOpen(true);

      const allUsers = await userAPI.getUsers({
        organization_id: project.organization_id,
        is_active: true,
        limit: 100,
      });

      // Exclude only direct members; team-based members should still be selectable
      const memberUserIds = new Set([
        ...members
          .filter((pm) => pm.membership_type === 'direct')
          .map((pm) => pm.user_id),
      ]);
      const filtered = allUsers.filter((u) => !memberUserIds.has(u.id));
      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Failed to load users for team:', error);
      toast.error('Failed to load users for this team');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAddMembers = async(userIds: string[], role: MemberRole = 'member') => {
    try {
      if (userIds.length === 0) {
        toast.error('Please select at least one user');
        return;
      }
      await projectMemberAPI.bulkAddMembers(
        projectId as string,
        userIds,
        role,
        "direct",
      );
  
      toast.success(`Added ${userIds.length} Direct member ${userIds.length !== 1 ? 's' : ''} to  the project`);
        setMemberModalOpen(false);
        await refreshMembers();
    } catch (error) {
      toast.error('Failed to add Direct members to the project');
    }
   
  }

  const refreshMembers = async () => {
    if (!projectId) {
      toast.error('No project Id details found')
      return
    }
    try {
      setLoading(true);
      const membersRes = await projectMemberAPI.getProjectMembers(projectId, true, { skip: 0, limit: 100 });
      setMembers(membersRes);
    } catch (error) {
      console.error('Failed to refresh members:', error);
      toast.error('Failed to refresh members');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="p-6 min-h-screen overflow-visible">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6" />
            Project Members
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage who has access to this project ({totalCount} member{totalCount !== 1 ? 's' : ''})
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isMainAdmin && (
            <>
              <button
                onClick={openMemberModal}
                className="btn btn-secondary flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Add Member
              </button>
              <button
                onClick={openAssignTeamModal}
                className="btn btn-primary flex items-center gap-2"
              >
                <UsersRound className="w-5 h-5" />
                Add Team
              </button>
            </>
          )}
        </div>
      </div>

      {/* Assigned Teams Section */}
      {assignedTeams.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 mr-1">Teams:</span>
          {assignedTeams.map((team) => (
            <span
              key={team.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors cursor-pointer group"
            >
              <UsersRound className="w-3.5 h-3.5" />
              <span
                onClick={() => navigate(`/teams/${team.id}?projectId=${projectId}`)}
                title={`Click to edit ${team.name}`}
              >
                {team.name}
              </span>
              {isMainAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setTeamToRemove(team);
                    setShowRemoveTeamModal(true);
                  }}
                  className="ml-0.5 p-0.5 rounded-full hover:bg-purple-300 text-purple-600 hover:text-purple-900 transition-colors"
                  title={`Remove ${team.name} from project`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Members List - scrollable view */}
      {/* Members List - scrollable view */}
      <div
        ref={contentRef}
        className="bg-white rounded-lg border border-gray-200 flex flex-col"
        style={{
          height: viewHeight ? `${viewHeight}px` : '70vh',
          maxHeight: 'calc(100vh - 200px)', // Added maxHeight as fallback
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">Loading members...</p>
            </div>
          </div>
        ) : members.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No members yet</h3>
              <p className="text-gray-600 mb-4">Assign a team to add all team members to this project</p>
              {isMainAdmin && (
                <button
                  onClick={openAssignTeamModal}
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <UsersRound className="w-5 h-5" />
                  Assign Team
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-auto">
              <MembersTable
                members={members}
                isAdmin={isMainAdmin}
                onUpdateRole={handleUpdateRole}
                // onRemoveMember={(memberId, email) => {
                //   setMemberToDelete({ id: memberId, email});
                //   setShowDeleteModal(true);
                // }}
                onRemoveMember={(memberId, email) => {
                  const memberObj = members.find(m => m.user_id === memberId);
                  const teamIdValue = (memberObj?.team_id === "None" ? null : memberObj?.team_id) || null;
                  setMemberToDelete({ 
                    id: memberId, 
                    email: email,
                    team_id: teamIdValue
                  });
                  
                  setShowDeleteModal(true);
                }}
                showActions={true}
                showJoinedDate={true}
                showRemoveAction={true}
                memberShipTypeDetails='direct'
              />
            </div>

            {/* Footer Pager - Fixed at bottom of container */}
            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 py-3">
              {(() => {
                const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
                const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
                const end = Math.min(totalCount, page * pageSize);
                return (
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Showing {start}-{end} of {totalCount} member{totalCount !== 1 ? 's' : ''}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <button
                          disabled={page === 1}
                          onClick={() => { const p = Math.max(1, page - 1); setPage(p); loadMembers(p); }}
                          className={`px-3 py-1 rounded border text-sm ${page === 1
                            ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                            : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          Previous
                        </button>
                        <span className="text-sm text-gray-600 px-2">
                          Page {page} of {totalPages}
                        </span>
                        <button
                          disabled={page >= totalPages}
                          onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); loadMembers(p); }}
                          className={`px-3 py-1 rounded border text-sm ${page >= totalPages
                            ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                            : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Assign Team Modal */}
      {showAssignTeamModal && isMainAdmin && (
        <AddTeamsModal
          isOpen={showAssignTeamModal}
          title="Assign Team to Project"
          description="Select a team to assign. All team members will be added to this project."
          availableTeams={availableTeams}
          loadingTeams={teamsLoading}
          allowMultiple={false}
          onClose={() => setShowAssignTeamModal(false)}
          onConfirm={handleAssignTeam}
          confirmLabel="Assign Team"
        />
      )}

      {/* Add Members Modal */}
    {memberModalOpen && (
  <AddMembersModal
    isOpen={memberModalOpen}
    title="Add Members to Project"
    description="Select organization users to add as project members."
    availableUsers={availableUsers}
    loadingUsers={usersLoading}
    showRoleSelector={false}
    initialRole="member"
    onClose={() => setMemberModalOpen(false)}
    onConfirm={handleAddMembers}
    confirmLabel="Add Members"
  />
    )}

      {showDeleteModal && memberToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Remove Member
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to remove
              <span className="font-medium"> {memberToDelete.email} </span>
              from this project?
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700"
                onClick={() => {
                  setShowDeleteModal(false);
                  setMemberToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2"
                disabled={isRemoving}
                onClick={async () => {
                  setIsRemoving(true);

                  await handleRemoveMember(memberToDelete);

                  setIsRemoving(false);
                  setShowDeleteModal(false);
                  setMemberToDelete(null);
                }}
              >
                {isRemoving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  "Remove"
                )}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* Remove Team Confirmation Modal */}
      {showRemoveTeamModal && teamToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Remove Team from Project
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to remove team
              <span className="font-semibold"> {teamToRemove.name} </span>
              from this project? All team-based members from this team will be removed.
            </p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700"
                disabled={isRemovingTeam}
                onClick={() => {
                  setShowRemoveTeamModal(false);
                  setTeamToRemove(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2"
                disabled={isRemovingTeam}
                onClick={handleRemoveTeam}
              >
                {isRemovingTeam ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  "Remove Team"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

